/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable functional/immutable-data */

import React, { useRef, useState } from 'react'
import IWorkSchedule from '../interfaces/IWorkSchedule'
import { useLocation, useHistory } from 'react-router-dom'
import Layout from '~/components/Layout'
import HeaderSummary from '~/features/Resources/Schedule/WorkSchedulePrint/components/HeaderSummary'
import { useLang } from '~/hooks/useLang'
import {
  Button,
  Container,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Card,
  CardHeader,
} from '@mui/material'
import dayjs from 'dayjs'
import { useReactToPrint } from 'react-to-print'
import TabSummary from '~/features/Dispatch/components/Summary/TabSummary'

const tableStyles = {
  container: {
    width: '100%',
    border: '1px solid #000',
    borderCollapse: 'collapse' as const,
    fontSize: '12px',
    '@media print': {
      fontSize: '10px',
      width: '100%',
      tableLayout: 'fixed' as const,
    },
  },
  headerCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    '@media print': {
      padding: '4px 2px',
      fontSize: '10px',
      backgroundColor: 'transparent',
    },
  },
  dataCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    color: '#000000DE',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    wordWrap: 'break-word' as const,
    '@media print': {
      padding: '3px 2px',
      fontSize: '0.875rem',
    },
  },
}

export default function Summary() {
  const { translate } = useLang()
  const location = useLocation<{
    readonly items?: readonly IWorkSchedule[]
    readonly searchType?: string
    readonly userId?: number
    readonly deviceId?: number
  }>()
  const history = useHistory()  
  const sessionData = React.useMemo(() => {
    const stored = sessionStorage.getItem('workSchedulePrintData')
    if (stored) {
      sessionStorage.removeItem('workSchedulePrintData') // Limpa após ler
      return JSON.parse(stored)
    }
    return null
  }, [])
  
  const data = sessionData?.items ?? location.state?.items ?? null
  const searchType = sessionData?.searchType ?? location.state?.searchType ?? null
  const paramId = sessionData?.userId ?? sessionData?.deviceId ?? location.state?.userId ?? location.state?.deviceId ?? null

  const ref = useRef<HTMLDivElement | null>(null)
  const [isPrinting, setIsPrinting] = useState(false)
  const canvasImagesRef = useRef<
    {
      canvas: HTMLCanvasElement
      img: HTMLImageElement
    }[]
  >([])


  const prepareCanvasesForPrint = () => {
    canvasImagesRef.current = []
    const root = ref.current as HTMLElement | null
    if (!root) return Promise.resolve()
    const canvases = Array.from(
      root.querySelectorAll('canvas')
    ) as readonly HTMLCanvasElement[]
    canvases.forEach((canvas) => {
      try {
        const dataUrl = canvas.toDataURL()
        const img = document.createElement('img')
        img.src = dataUrl
        img.style.width = canvas.style.width || `${canvas.width}px`
        img.style.height = canvas.style.height || `${canvas.height}px`
        img.className = 'print-replacement'
        canvas.style.display = 'none'
        canvas.parentNode?.insertBefore(img, canvas)
        canvasImagesRef.current.push({ canvas, img })
      } catch (err) {
        // intentionally ignore errors from tainted canvases
      }
    })

    return new Promise((res) => {
      setIsPrinting(true)
      setTimeout(res, 250)
    })
  }

  const restoreCanvases = () => {
    canvasImagesRef.current.forEach(({ canvas, img }) => {
      img.parentNode?.removeChild(img)
      canvas.style.display = ''
    })
    canvasImagesRef.current = []
    setIsPrinting(false)
  }

  const pageStyle = `
    @page { size: A4; margin: 0; }  // Alterado de 5mm para 0
    @media print {
      html, body { height: auto !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; }
      .print-container { margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact; }
      .print-container * { margin: 0 !important; padding: 0 !important; overflow: visible !important; }
      /* Removido .page-break para evitar quebras desnecessárias; adicione manualmente se precisar */
      /* Ajustes para tabelas e containers */
      table, thead, tbody, tr, td, th { page-break-inside: avoid; break-inside: avoid; margin: 0 !important; padding: 0 !important; }
      .MuiTableContainer-root { max-height: none !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; }
      /* Ajustes específicos para a tabela de detalhes */
      table { width: 100% !important; table-layout: fixed !important; margin: 0 !important; padding: 0 !important; }
      td, th { word-wrap: break-word !important; overflow-wrap: break-word !important; margin: 0 !important; padding: 0 !important; }
      /* Garantir que canvases sejam visíveis */
      canvas { display: block !important; margin: 0 !important; padding: 0 !important; }
    }
  `

  const handlePrint = useReactToPrint({
    content: () => ref.current,
    documentTitle: translate('Schedule'),
    pageStyle,
    removeAfterPrint: false,
    copyStyles: true,
    onBeforeGetContent: () => prepareCanvasesForPrint(),
    onAfterPrint: () => restoreCanvases(),
  })

  if (!data) {
    return (
      <Layout marginLayout>
        <Card sx={{ boxShadow: 4, mb: 2 }}>
          <CardHeader title={translate('WorkSchedule Summary')} />
        </Card>
        <Container sx={{ padding: 2 }}>
          <Typography variant="body1">
            {translate('No data available — perform a search first')}
          </Typography>
          <Button
            sx={{ mt: 2 }}
            variant="contained"
            onClick={() => history.push('/workSchedule-Print')}
          >
            {translate('Back')}
          </Button>
        </Container>
      </Layout>
    )
  }

  return (
    <div>
      <Button onClick={handlePrint} sx={{ mb: 2 }}>
        {translate('Print')}
      </Button>

      <div
        ref={ref}
        className="print-container"
        style={{
          margin: '0',
          padding: '0',
        }}
        data-printing={String(isPrinting)}
      >
        <HeaderSummary
          headerSummary={(() => {
            const items = data ?? []
            const startDates = items
              .map((row: IWorkSchedule) => row.start_date)
              .filter(Boolean)
            const endDates = items.map((row: IWorkSchedule) => row.end_date).filter(Boolean)
            const dateBegin = startDates.length
              ? startDates.reduce((dateA: string, dateB: string) =>
                  dateA < dateB ? dateA : dateB
                )
              : undefined
            const dateEnd = endDates.length
              ? endDates.reduce((dateA: string, dateB: string) =>
                  dateA > dateB ? dateA : dateB
                )
              : undefined

            const groupsMap = new Map<
              number,
              { readonly id: number; readonly name: string }
            >()
            items.forEach((row: IWorkSchedule) => {
              const group = row.dispatch_group
              if (group && !groupsMap.has(group.id))
                groupsMap.set(group.id, { id: group.id, name: group.name })
            })

            let coordinator = {
              id: 0,
              name: '',
              cpf: '',
              rank: { name: null },
              registration: '',
              representation_name: '',
            }
            if (searchType === 'user' && paramId) {
              for (const row of items) {
                const crew = row.crew ?? []
                const found = crew.find(
                  (crewMember: { user?: { id?: number; name?: string; registration?: string; representation_name?: string } }) => crewMember.user?.id === paramId
                )
                if (found) {
                  coordinator = {
                    id: found.user.id ?? 0,
                    name: found.user.name ?? '',
                    cpf: '',
                    rank: { name: null },
                    registration: found.user.registration ?? '',
                    representation_name:
                      found.user.representation_name ?? found.user.name ?? '',
                  }
                  break
                }
              }
            }

            let scheduleName = items[0]?.name ?? ''

            if (searchType === 'device' && items[0]?.device?.name) {
              scheduleName = items[0].device.name
            }

            if (searchType === 'user' && paramId) {
              for (const row of items) {
                const crewMember = (row.crew || []).find(
                  (crew: { user?: { id?: number; name?: string; representation_name?: string } }) => crew.user?.id === paramId
                )
                if (crewMember && crewMember.user) {
                  scheduleName =
                    crewMember.user.representation_name || crewMember.user.name || ''
                  break
                }
              }
            }

            return {
                name: scheduleName,
                coordinator,
                date_begin: dateBegin ?? '',
                date_expected_closing: dateEnd ?? '',
                date_end: dateEnd ?? '',
                dispatch_groups: Array.from(groupsMap.values()),
              }
          })()}
          showRegistration={searchType !== 'device'}
        />
        <TabSummary title={searchType === 'user' ? translate('User Schedule') : translate('Device Schedule')}>
          <Table size="small" sx={tableStyles.container}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableStyles.headerCell, width: '11%' }}>
                  {translate('Initial Date')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: '11%' }}>
                  {translate('Finish Date')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: '5%' }}>
                  {translate('Hours')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: '10%' }}>
                  {translate('Devices')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: '11%' }}>
                  {translate('Dispatch Group')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.headerCell, width: '8%' }}>
                  {translate('Performance')}
                </TableCell>
                {searchType === 'user' && (
                  <TableCell sx={{ ...tableStyles.headerCell, width: '15%' }}>
                    {translate('Position')}
                  </TableCell>
                )}
                <TableCell sx={{ ...tableStyles.headerCell, width: searchType === 'user' ? '19%' : '25%' }}>
                  {translate('Crew Composition')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row: IWorkSchedule) => (
                <TableRow key={row.id}>
                  <TableCell sx={tableStyles.dataCell}>
                    {row.start_date
                      ? dayjs(row.start_date).format('DD/MM/YYYY HH:mm')
                      : ''}
                  </TableCell>
                  <TableCell sx={tableStyles.dataCell}>
                    {row.end_date
                      ? dayjs(row.end_date).format('DD/MM/YYYY HH:mm')
                      : ''}
                  </TableCell>
                  <TableCell sx={tableStyles.dataCell}>
                    {row.work_shift?.work_hours ?? ''}
                  </TableCell>
                  <TableCell sx={tableStyles.dataCell}>
                    {row.device?.name ?? ''}
                  </TableCell>
                  <TableCell sx={tableStyles.dataCell}>
                    {row.dispatch_group?.name ?? ''}
                  </TableCell>
                  <TableCell sx={tableStyles.dataCell}>
                    {row.task ?? ''}
                  </TableCell>
                  {searchType === 'user' && (
                    <TableCell sx={tableStyles.dataCell}>
                      {(() => {
                        const crewMember = (row.crew || []).find(
                          (c) => c.user?.id === paramId
                        )
                        return crewMember?.position?.name ?? ''
                      })()}
                    </TableCell>
                  )}
                  <TableCell sx={tableStyles.dataCell}>
                    {(row.crew || [])
                      .map((crewMember) => {
                        const name = crewMember.user?.representation_name ?? crewMember.user?.name ?? ''
                        const pos = crewMember.position?.name ? ` [${crewMember.position.name}]` : ''
                        return `${name}${pos}`
                      })
                      .join('; ')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabSummary>
        <div className="page-break" />
      </div>
    </div>
  )
}
